import React from "react";
import { withTranslation } from "react-i18next";

import Breadcrumbs from "components/Common/Breadcrumb";
import { Container, Card, CardBody, Button, Row, Col, Alert } from "reactstrap";
import { useParams } from "react-router-dom";
import uploadIcon from "assets/icons/upload.svg";
import eyeIcon from "assets/icons/eye.svg";
import { upload } from "components/modal/UploadModalService";
import { formatCurrency } from "utils/formatters";
import { MONTH } from "helpers/fn_helper";
import ShimmerLoading from "components/Common/Shimmer";
import { getImageUrl, ImageSourceType } from "utils/imageUtils";

// API
import { useGetEarningPartnerQuery } from "services/transactions/api";
import { useUploadEvidenceMutation, useDeleteEvidenceMutation } from "services/payments/api";

import EarningPartnerTab from "./EarningPartnerTab";

const EarningPartner = (props) => {
  //meta title
  document.title = `${props.t("PartnersTxnInfo")} | ${process.env.REACT_APP_CMS_TITLE}`;
  const { id } = useParams();

  // API
  const { data, error, isLoading, refetch } = useGetEarningPartnerQuery(id);
  const [uploadEvidence] = useUploadEvidenceMutation();
  const [deleteEvidence] = useDeleteEvidenceMutation();

  // UPLOAD
  const handleUpload = async () => {
    const evidence = data.paymentEvidences?.length > 0 ? data.paymentEvidences.map((item) => getImageUrl(item.imageUrl, { type: ImageSourceType.LOCAL, path: "payment-evidence" })) : [];
    const result = await upload.openImage({
      initFiles: evidence,
      title: "อัปโหลดหลักฐานการชำระเงิน",
      desc: "อัปโหลดรูปภาพไฟล์สลิปใบเสร็จการชำระเงินค่าคอมมิชชันของพาร์ทเนอร์ได้ที่ด้านล่างนี้",
      message: "อัพโหลดรูปภาพหลักฐานการโอนเงิน",
      accept: "PNG, JPG, JPEG, WEBP",
      maxFile: 1,
      maxSize: 5 * 1024 * 1024,
      immediateAction: false,
      onUploadFile: onUploadFile,
      onRemoveFile: onRemoveFile,
    });
  };

  const onRemoveFile = async (file) => {
    console.log('on remove file', file);
    const evidence = data.paymentEvidences?.find((item) => item.imageUrl === file.name);
    if (!evidence) {
      console.error('Evidence not found for file:', file);
      return;
    }
    
    try {
      await deleteEvidence({ id: evidence.id }).unwrap();
      // Refresh the data after successful deletion
      await refetch();
    } catch (error) {
      console.error("Failed to delete evidence:", error);
      throw error; // Re-throw to handle in the UI
    }
  };

  const onUploadFile = async (file, onProgress) => {
    let progressInterval;
    console.log('on upload file', file);
    
    try {
      // Simulate progress
      await new Promise((resolve) => {
        let progress = 0;
        progressInterval = setInterval(() => {
          if (progress < 90) {
            progress += 19;
            const safeProgress = Math.min(100, Math.max(0, progress));
            onProgress(safeProgress);
          } else {
            clearInterval(progressInterval);
            resolve();
          }
        }, 300);
      });

      // Upload the file
      const response = await uploadEvidence({
        file,
        earningsId: id,
      }).unwrap();

      clearInterval(progressInterval);
      onProgress(100);

      // Refresh the data after successful upload
      await refetch();

      // Return the file URL from the response
      return response.image_url || response.url;
    } catch (error) {
      console.error("Upload error:", error);
      clearInterval(progressInterval);
      const errorMessage = error.data?.message || "อัพโหลดไฟล์ไม่สำเร็จ";
      throw new Error(errorMessage);
    }
  };

  // RENDER
  function renderContentText(title, content) {
    return (
      <>
        <Col xs={5}>
          <div className="d-flex gap-1 align-items-center text-xl text-gray-500">
            <div className="fw-semibold">{title}</div>
            <div className="fw-semibold">:</div>
          </div>
        </Col>
        <Col xs={6}>
          <div className="text-xl text-gray-500">{content}</div>
        </Col>
      </>
    );
  }

  return (
    <div className="page-content" data-testid="earning-partner-page">
      <Container fluid>
        <Breadcrumbs title={props.t("PartnersTxnInfo")} breadcrumbItem={props.t("PartnersTxnInfo")} back="กลับหน้ารายการธุรกรรม" />

        {/* Add your EarningPartner management content here */}
        <Card className="card-info mt-4">
          {isLoading ? (
            <div className="text-center">
              <ShimmerLoading fullWidth={true} height={600} />
            </div>
          ) : error ? (
            <Alert color="danger">{error.message || "เกิดข้อผิดพลาดในการโหลดข้อมูล"}</Alert>
          ) : (
            <>
              {data && (
                <CardBody>
                  <Row>
                    <Col lg={12} xl={{ order: 2, size: 6 }}>
                      <div className="d-flex gap-2 align-items-center justify-content-center justify-content-xl-end text-gray-600">
                        <h2>สถานะ</h2>
                        <h2>:</h2>
                        <h2 className="fw-semibold">{data.status === "Pending" ? "รอดำเนินการ" : data.status === "Paid" ? "ดำเนินการแล้ว" : "ยกเลิก"}</h2>
                      </div>
                      <div className="d-flex gap-2 align-items-center justify-content-center justify-content-xl-end text-gray-600">
                        <h4 className="text-danger-600">***ค่าคอมมิชชันจะถูกตัดรอบทุกวันที่ 30-31 ของเดือน</h4>
                      </div>
                      <div className="d-flex gap-2 align-items-center justify-content-center justify-content-xl-end text-gray-600">
                        <h3>ค่าคอมมิชชันที่จะได้ :</h3>
                        <h1 className="text-primary-600 fw-semibold">{formatCurrency(data.totalEarnings - (data.totalEarnings * 0.03))}</h1>
                      </div>
                      <div className="d-flex gap-2 align-items-center justify-content-center justify-content-xl-end text-gray-600">
                        <div className="text-xl fw-semibold">(ก่อนหักภาษี ณ ที่จ่าย 3% {formatCurrency(data.totalEarnings)} หักภาษี {formatCurrency(data.totalEarnings * 0.03)})</div>
                      </div>
                    </Col>
                    <Col lg={12} xl={{ order: 1, size: 6 }}>
                      <Row className="gap-2">
                        <Col sm={12}>
                          <h3 className="fw-semibold text-gray-700">ข้อมูลของพาร์ทเนอร์</h3>
                        </Col>
                        <Col sm={12}>
                          <h1 className="fw-semibold text-gray-600">{`${data.partner.firstName} ${data.partner.lastName}`}</h1>
                        </Col>
                        {renderContentText("ไอดีพาร์ทเนอร์", data.partner.id)}
                        {renderContentText("ชื่อบัญชีผู้ใช้", data.partner.email)}
                        {renderContentText("ประเภท Affiliator", data.partner.partnerType === "personal" ? "บุคคลทั่วไป" : "บริษัท")}
                      </Row>
                    </Col>
                  </Row>
                  <Row>
                    <Col lg={12} xl={6}>
                      <Row className="gap-2 mt-4">
                        <Col sm={12}>
                          <h3 className="fw-semibold text-gray-700">ข้อมูลบัญชีธนาคาร</h3>
                        </Col>
                        <Col sm={12}>
                          <h1 className="fw-semibold text-gray-600">{data.bankAccounts?.[0]?.accountNumber || "-"}</h1>
                        </Col>
                        {renderContentText("เลขที่บัญชี", data.bankAccounts?.[0]?.accountNumber || "-")}
                        {renderContentText("ชื่อ-นามสกุล เจ้าของบัญชี*", data.bankAccounts?.[0]?.accountName || "-")}
                        {renderContentText("ธนาคารที่ใช้สมัครบัญชี", data.bankAccounts?.[0]?.bankName || "-")}
                        {renderContentText("รหัสสาขา", data.bankAccounts?.[0]?.branchCode || "-")}
                      </Row>
                    </Col>
                    <Col lg={12} xl={6}>
                      <Row className="gap-2 mt-4">
                        <Col sm={12}>
                          <h3 className="fw-semibold text-gray-700">รายได้ประจำเดือน</h3>
                        </Col>
                        <Col sm={12}>
                          <h1 className="fw-semibold text-gray-600">
                            {(() => {
                              const date = new Date(data.monthYear);
                              const year = date.getFullYear() + 543; // แปลงเป็นปี พ.ศ.
                              const month = MONTH[date.getMonth()].label;
                              return `${month} ${year}`;
                            })()}
                          </h1>
                        </Col>
                        {/* {renderContentText("คำสั่งซื้อทั้งหมด", `${data.totalOrders} รายการ`)} */}
                        {renderContentText("ยอคอมมิชชัน (ก่อนหักภาษี)", formatCurrency(data.totalEarnings))}
                        {renderContentText("- ค่าคอมมิชชัน", formatCurrency(data.totalCommission))}
                        {renderContentText("- โบนัส", formatCurrency(data.totalBonus))}
                        {renderContentText("ภาษี ณ ที่จ่าย 3%", formatCurrency(data.totalEarnings * 0.03))}
                        {renderContentText("ยอดค่าคอมมิชชันทั้งหมด", formatCurrency(data.totalEarnings - (data.totalEarnings * 0.03)))}
                      </Row>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Button 
                        color="primary" 
                        onClick={handleUpload} 
                        className="mt-4 gap-2"
                        disabled={!data.bankAccounts?.length}
                      >
                        {data.paymentEvidences?.length > 0 ? (
                          <>
                            <img src={eyeIcon} alt="upload" />
                            ดูสลิปการชำระเงิน
                          </>
                        ) : (
                          <>
                            <img src={uploadIcon} alt="upload" />
                            อัปโหลดสลิปการชำระเงิน
                          </>
                        )}
                      </Button>
                    </Col>
                  </Row>
                </CardBody>
              )}
            </>
          )}
        </Card>

        <EarningPartnerTab data={data} />
      </Container>
    </div>
  );
};

export default withTranslation()(EarningPartner);
